import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ShieldCheck, Activity, ScrollText, Download, Trash2, RefreshCw, Loader2, CheckCircle2, AlertTriangle } from "lucide-react";
import { fmtDate } from "@/lib/format";

const isOrg = (u) => (u?.email || "").toLowerCase().endsWith("@trifitindia.com") || u?.role === "owner";

function Card({ icon: Icon, title, subtitle, children, testid }) {
  return (
    <div className="border border-border mb-6" data-testid={testid}>
      <div className="px-5 py-3 border-b border-border flex items-center gap-2">
        <Icon className="w-4 h-4" />
        <span className="font-heading font-bold tracking-tight">{title}</span>
        {subtitle && <span className="text-xs text-muted-foreground ml-auto">{subtitle}</span>}
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

export default function Compliance() {
  const { user } = useAuth();
  const [health, setHealth] = useState(null);
  const [consent, setConsent] = useState(null);
  const [audit, setAudit] = useState(null);
  const [running, setRunning] = useState(false);
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => {
    api.get("/system/health").then((r) => setHealth(r.data)).catch(() => {});
    api.get("/compliance/consent").then((r) => setConsent(r.data)).catch(() => {});
    api.get("/compliance/audit").then((r) => setAudit(r.data)).catch(() => {});
  }, []);
  useEffect(() => { if (isOrg(user)) load(); }, [user, load]);

  if (!isOrg(user)) {
    return (
      <div className="p-12 text-center" data-testid="compliance-restricted">
        <ShieldCheck className="w-10 h-10 mx-auto text-muted-foreground" />
        <h1 className="font-heading text-2xl font-black mt-4">Restricted</h1>
        <p className="text-sm text-muted-foreground mt-2">This area is limited to Trifit India administrators (@trifitindia.com).</p>
      </div>
    );
  }

  const runMaint = async () => {
    setRunning(true);
    try { await api.post("/system/run-maintenance"); toast.success("Resync + integrity check started · refresh in a moment"); setTimeout(load, 4000); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setRunning(false); }
  };
  const exportAthlete = async (id, name) => {
    setBusy(`exp-${id}`);
    try {
      const r = await api.get(`/athletes/${id}/export`);
      const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `trifitxps-export-${id}.json`; a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${name}`); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Export failed"); }
    finally { setBusy(null); }
  };
  const deleteAthlete = async (id, name) => {
    if (!window.confirm(`Erase ALL local Trifit.XPS data for ${name}? This does NOT touch their Trifit phone account. This cannot be undone.`)) return;
    setBusy(`del-${id}`);
    try {
      const r = await api.delete(`/athletes/${id}/local-data?confirm=true`);
      toast.success(`Erased ${name} · ${Object.values(r.data.erased).reduce((a, b) => a + b, 0)} records`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
    finally { setBusy(null); }
  };

  return (
    <div>
      <header className="h-16 border-b border-border flex items-center px-8 gap-4 sticky top-0 bg-background z-10">
        <ShieldCheck className="w-5 h-5" />
        <h1 className="font-heading text-xl font-black tracking-tight">Data privacy</h1>
        <span className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground">Trifit India only</span>
      </header>

      <div className="p-8 max-w-5xl">
        <div className="border-l-2 border-foreground pl-4 mb-8 max-w-3xl" data-testid="privacy-note">
          <p className="text-xs tracking-[0.18em] uppercase font-bold text-muted-foreground">How we treat athlete data</p>
          <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
            Athlete data belongs to the athlete and to the squad that collected it. Sharing is consent-based and can be
            withdrawn, every record can be exported or erased on request, and staff only see the domains their role
            covers — medical stays with the athlete and their doctor. Actions that touch athlete data are written to an
            append-only log. We do not sell data or use it to train anything outside your own squad.
          </p>
          <p className="text-[11px] text-muted-foreground mt-2">
            This describes what the system actually does today, not a certification claim. Read the{" "}
            <Link to="/privacy" target="_blank" rel="noreferrer" className="text-foreground underline underline-offset-2">privacy policy</Link>{" "}
            for the legal wording.
          </p>
        </div>
        {/* System health & auto-sync */}
        <Card icon={Activity} title="System Health & Auto-Sync" subtitle={health ? `nightly 03:00 UTC · debounce ${health.debounce_sec}s` : ""} testid="compliance-health">
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <span className={`text-xs px-2 py-1 border ${health?.scheduler ? "border-traffic-green text-traffic-green" : "border-traffic-red text-traffic-red"}`} data-testid="health-scheduler">Scheduler {health?.scheduler ? "ON" : "OFF"}</span>
            <span className={`text-xs px-2 py-1 border ${health?.openwearables_configured ? "border-traffic-green text-traffic-green" : "border-traffic-amber text-traffic-amber"}`} data-testid="health-openwearables">OpenWearables {health?.openwearables_configured ? "connected" : "not configured"}</span>
            <button data-testid="run-maintenance-btn" onClick={runMaint} disabled={running} className="ml-auto text-xs border border-foreground px-3 py-1.5 font-bold uppercase tracking-wider hover:bg-foreground hover:text-background transition-colors disabled:opacity-50 flex items-center gap-1.5">
              {running ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Run sync + integrity now
            </button>
          </div>
          <div className="border border-border overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-border text-[10px] tracking-[0.15em] uppercase font-bold text-muted-foreground">
                <th className="text-left p-3">Athlete</th><th className="text-left p-3">Last OK</th><th className="text-left p-3">Trigger</th><th className="text-left p-3">Integrity</th>
              </tr></thead>
              <tbody data-testid="health-table">
                {(health?.athletes || []).length === 0 ? <tr><td colSpan={4} className="p-3 text-muted-foreground">No sync records yet · run it once.</td></tr> :
                  (health.athletes || []).map((h) => {
                    const intg = h.integrity;
                    const ok = intg?.coherent;
                    return (
                      <tr key={h.athlete_id} className="border-b border-border last:border-0" data-testid={`health-row-${h.athlete_id}`}>
                        <td className="p-3 font-medium">{h.athlete_id}</td>
                        <td className="p-3 text-muted-foreground tabular-nums">{h.last_ok ? new Date(h.last_ok).toLocaleString() : "-"}</td>
                        <td className="p-3 text-muted-foreground">{h.trigger || "-"}</td>
                        <td className="p-3">
                          {!intg ? <span className="text-muted-foreground">-</span> :
                            ok ? <span className="text-traffic-green flex items-center gap-1 text-xs"><CheckCircle2 className="w-3.5 h-3.5" /> coherent</span> :
                            <span className="text-traffic-amber flex items-center gap-1 text-xs" title={(intg.issues || []).join("; ")}><AlertTriangle className="w-3.5 h-3.5" /> {(intg.issues || [])[0] || "check"}</span>}
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </Card>

        {/* Consent + Data rights */}
        <Card icon={ShieldCheck} title="Consent & Data Rights" subtitle="GDPR / DPDP" testid="compliance-consent">
          <div className="border border-border overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-border text-[10px] tracking-[0.15em] uppercase font-bold text-muted-foreground">
                <th className="text-left p-3">Athlete</th><th className="text-left p-3">Trifit linked</th><th className="text-left p-3">Data sharing</th><th className="text-left p-3">Consent at</th><th className="text-right p-3">Data rights</th>
              </tr></thead>
              <tbody data-testid="consent-table">
                {(consent?.athletes || []).map((a) => (
                  <tr key={a.athlete_id} className="border-b border-border last:border-0" data-testid={`consent-row-${a.athlete_id}`}>
                    <td className="p-3 font-medium">{a.name}</td>
                    <td className="p-3 text-muted-foreground">{a.trifit_email ? "yes" : "no"}</td>
                    <td className="p-3">{a.data_sharing ? <span className="text-traffic-green">on</span> : <span className="text-muted-foreground">off</span>}</td>
                    <td className="p-3 text-muted-foreground tabular-nums">{fmtDate(a.consent_at)}</td>
                    <td className="p-3 text-right whitespace-nowrap">
                      <button data-testid={`export-${a.athlete_id}`} onClick={() => exportAthlete(a.athlete_id, a.name)} disabled={busy === `exp-${a.athlete_id}`} className="text-xs border border-border px-2 py-1 hover:bg-surface transition-colors inline-flex items-center gap-1 disabled:opacity-50"><Download className="w-3 h-3" /> Export</button>
                      <button data-testid={`delete-${a.athlete_id}`} onClick={() => deleteAthlete(a.athlete_id, a.name)} disabled={busy === `del-${a.athlete_id}`} className="ml-2 text-xs border border-traffic-red text-traffic-red px-2 py-1 hover:bg-traffic-red/10 transition-colors inline-flex items-center gap-1 disabled:opacity-50"><Trash2 className="w-3 h-3" /> Erase</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-[11px] text-muted-foreground mt-2">Erase removes only local Trifit.XPS data (right to erasure). The athlete's Trifit phone account is never modified.</p>
        </Card>

        {/* Audit log */}
        <Card icon={ScrollText} title="Audit Log" subtitle={audit ? `${audit.count} recent actions` : ""} testid="compliance-audit">
          <div className="border border-border divide-y divide-border max-h-80 overflow-y-auto" data-testid="audit-list">
            {(audit?.entries || []).length === 0 ? <div className="p-3 text-sm text-muted-foreground">No audited actions yet. Sensitive actions (score pushes, wearable connect, exports, deletions) are logged here.</div> :
              (audit.entries || []).map((e, i) => (
                <div key={i} className="p-3 flex items-center gap-3 text-sm" data-testid={`audit-row-${i}`}>
                  <span className="text-xs text-muted-foreground w-40 tabular-nums">{new Date(e.at).toLocaleString()}</span>
                  <span className="text-xs uppercase tracking-wider font-bold w-48">{e.action}</span>
                  <span className="text-muted-foreground flex-1 truncate">{e.target}</span>
                  <span className="text-xs text-muted-foreground">{e.user}</span>
                </div>
              ))}
          </div>
        </Card>

      </div>
    </div>
  );
}
